import { useEffect, useState } from 'react'

import ReactCountryFlag from 'react-country-flag'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import styled from 'styled-components'

import { Button } from 'components/Button/Button'
import DropdownSelect from 'features/DropdownSelect/DropdownSelect'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { AllIcons } from 'helpers/allIcons'
import { Icons } from 'helpers/icons'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import {
    getCountries,
    getGeoBlock,
    setCountries
} from 'services/endpoints/country'
import colors from 'styles/colors'
import { type Country, type Region } from 'types/api/country'

export const GeoBlocking = () => {
    const [blockActive, setBlockActive] = useState<boolean>(false)
    const [searchTerm, setSearchTerm] = useState<string>('')
    const [botSearchTerm, setBotSearchTerm] = useState<string>('')
    const [selectedCountry, setSelectedCountry] = useState<Country | null>(null)
    const [selectedRegion, setSelectedRegion] = useState<Region | null>(null)
    const [countryRegions, setCountryRegions] = useState<Region[]>([])

    const [showSuccess, setShowSuccess] = useState<boolean>(false)

    const [allCountries, setAllCountries] = useState<Country[]>([])

    const [blockedCountriesIds, setBlockedCountriesIds] = useState<number[]>([])
    const [blockedRegionsIds, setBlockedRegionsIds] = useState<number[]>([])

    const [shouldRemove, setShouldRemove] = useState<boolean>(false)

    const queryClient = useQueryClient()
    const { t } = useTranslation()

const { data: countriesData } = useQuery(
    ['allCountries'],
    getCountries,
    {
        refetchOnWindowFocus: false
    }
)

const { data: geoBlockData } = useQuery(
    ['geoBlock'],
    getGeoBlock,
    {
        refetchOnWindowFocus: false
    }
)
    const addGeoBlock = useMutation(
        async (geoData: {
            geo_block?: boolean
            countries: number[]
            regions: number[]
        }) => {
            return await setCountries(geoData)
        },
        {
            onMutate: async resData => {
                const { countries, regions } = resData
                const previousValue =
                    queryClient.getQueryData<Country[]>('allCountries')

                const newValue = previousValue?.map(country => {
                    if (
                        country.blocked === 0 &&
                        countries.includes(country.id)
                    ) {
                        const tmp = country.regions.map(region => {
                            return { ...region, blocked: 0 }
                        })
                        return { ...country, blocked: 1, regions: tmp }
                    }

                    if (country.blocked === 1) {
                        const shouldUnblockCountry = country.regions.some(
                            region => {
                                return regions.includes(region.id)
                            }
                        )
                        if (shouldUnblockCountry) {
                            const tmpRegions = blockRegion(
                                country.regions,
                                regions
                            )
                            return {
                                ...country,
                                blocked: 0,
                                regions: tmpRegions
                            }
                        }

                        return country
                    }
                    const tmpRegions = blockRegion(country.regions, regions)
                    return { ...country, regions: tmpRegions }
                })

                queryClient.setQueryData('allCountries', newValue)
                !showSuccess && setShowSuccess(true)
                selectedCountry && setSelectedCountry(null)
                selectedRegion && setSelectedRegion(null)
                searchTerm && setSearchTerm('')
                botSearchTerm && setBotSearchTerm('')

                return previousValue
            },
            onError: previousValue => {
                queryClient.setQueryData('allCountries', previousValue)
            },
            onSettled: () => {}
        }
    )

    const deleteGeoBlock = useMutation(
        async (geoData: { countries: number[]; regions: number[] }) => {
            return await setCountries(geoData)
        },
        {
            onMutate: async geoData => {
                const { countries, regions } = geoData
                const previousValue =
                    queryClient.getQueryData<Country[]>('allCountries')
                const newValue = previousValue?.map(el => {
                    if (el.blocked === 1 && !countries.includes(el.id)) {
                        return { ...el, blocked: 0 }
                    }

                    const tmpRegions = el.regions.map(region => {
                        if (
                            region.blocked === 1 &&
                            !regions.includes(region.id)
                        ) {
                            return { ...region, blocked: 0 }
                        }
                        return region
                    })

                    return { ...el, regions: tmpRegions }
                })

                queryClient.setQueryData('allCountries', newValue)
            },
            onSettled: () => {
                shouldRemove && setShouldRemove(false)
            }
        }
    )

    const blockRegion = (
        countryRegions: Region[],
        blockedIndexes: number[]
    ) => {
        return countryRegions.map(region => {
            if (region.blocked === 0 && blockedIndexes.includes(region.id)) {
                return { ...region, blocked: 1 }
            }
            return region
        })
    }

    const configData = () => {
        if (countriesData && Array.isArray(countriesData)) {
            const countryArray = countriesData.map(country => ({
                id: country.id,
                name: country.name,
                code: country.code,
                regions: country.regions
            }))

            if (searchTerm.length === 0) {
                return countryArray
            }

            return countryArray.filter(item =>
                item.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
        }

        return []
    }

useEffect(() => {
    if (!countriesData || !geoBlockData) {
        return
    }

    const blockedCountries = geoBlockData.countries ?? []
    const blockedRegions = geoBlockData.regions ?? []

    const countries = countriesData.map(country => ({
        ...country,
        blocked: blockedCountries.includes(country.id) ? 1 : 0,
        regions: country.regions.map(region => ({
            ...region,
            blocked: blockedRegions.includes(region.id) ? 1 : 0
        }))
    }))

    setBlockActive(Boolean(geoBlockData.geo_block))
    setBlockedCountriesIds(blockedCountries)
    setBlockedRegionsIds(blockedRegions)
    setAllCountries(countries)
}, [countriesData, geoBlockData])

    useEffect(() => {
        setSelectedRegion(null)
        if (selectedCountry) {
            const tmpRegions = selectedCountry.regions.filter(region => {
                return !blockedRegionsIds.includes(region.id)
            })
            setCountryRegions(tmpRegions)
        }
    }, [selectedCountry])

    useEffect(() => {
        shouldRemove &&
            deleteGeoBlock.mutate({
                countries: blockedCountriesIds,
                regions: blockedRegionsIds
            })
    }, [shouldRemove])

    useEffect(() => {
        if (selectedCountry) {
            if (selectedRegion) {
                if (blockedCountriesIds.includes(selectedCountry.id)) {
                    const tmpCountries = blockedCountriesIds.filter(bc => {
                        return bc !== selectedCountry.id
                    })
                    setBlockedCountriesIds(tmpCountries)
                }
            } else {
                let tmp: number[] = []
                selectedCountry.regions.forEach(region => {
                    if (blockedRegionsIds.includes(region.id)) {
                        tmp = blockedRegionsIds.filter(br => br !== region.id)
                        setBlockedRegionsIds(tmp)
                    }
                })
            }
        }
    }, [
        selectedCountry,
        selectedRegion,
        blockedRegionsIds,
        blockedCountriesIds
    ])

    const handleCountryDelete = (countryId: number) => {
        const tmpCountries = blockedCountriesIds.filter(id => id !== countryId)
        setBlockedCountriesIds(tmpCountries)
        setShouldRemove(true)
    }
    const handleRegionDelete = (regionId: number) => {
        const tmpRegions = blockedRegionsIds.filter(id => id !== regionId)
        setBlockedRegionsIds(tmpRegions)
        setShouldRemove(true)
    }

    const handleAddBlock = () => {
        addGeoBlock.mutate({
            geo_block: blockActive,

            countries:
                selectedCountry && !selectedRegion
                    ? [...blockedCountriesIds, selectedCountry.id]
                    : blockedCountriesIds,

            regions: selectedRegion
                ? [...blockedRegionsIds, selectedRegion.id]
                : blockedRegionsIds
        })
    }
    return (
        <WithHeaderSection
            headerSection={
                <LayoutHeader
                    type='switch'
                    section={t('privacySecurity')}
                    title={t('geoBlocking')}
                    switchActive={blockActive}
                    switchFn={() => {
                        const nextValue = !blockActive

                        showSuccess && setShowSuccess(false)

                        setBlockActive(nextValue)

                        setCountries({
                            geo_block: nextValue,
                            countries: blockedCountriesIds,
                            regions: blockedRegionsIds
                        }).catch(() => {
                            setBlockActive(blockActive)
                        })
                    }}
                />
            }
        >
            {blockActive ? (
                <>
                    {showSuccess ? (
                        <ConfirmationDialog>
                            <img
                                src={AllIcons.geoblock_success}
                                alt='dialog icon'
                            />
                            <h3>{`${t('success')}!`}</h3>
                            <p>{`${t(
                                'youHaveBlockedTheLocation'
                            )} ${selectedRegion?.name} ${selectedCountry?.name}`}</p>
                            <Button
                                text={t('close')}
                                color='black'
                                font='mont-16-bold'
                                height='5'
                                width='12'
                                clickFn={() => {
                                    setShowSuccess(false)
                                }}
                            />
                        </ConfirmationDialog>
                    ) : (
                        <>
                            <DropdownSelect
                                icon={AllIcons.globe_black}
                                placeHolder={t('selectCountry')}
                                searchTerm={searchTerm}
                                setSearchTerm={setSearchTerm}
                                elementsArr={configData()}
                                topSectionSelected={selectedCountry}
                                topSectionSelect={setSelectedCountry}
                                hasBottomSection={!!selectedCountry}
                                botIcon={AllIcons.discovery_black}
                                botSearchTerm={botSearchTerm}
                                setBotSearchTerm={setBotSearchTerm}
                                botElementsArr={countryRegions}
                                botSectionSelected={selectedRegion}
                                botSectionSelect={setSelectedRegion}
                            />
                            <div className='blockedListAndButton'>
                                <div className='blocked__list'>
                                    {allCountries.map(country => {
                                        const blockedRegions =
                                            country.regions.filter(
                                                region => region.blocked === 1
                                            )

                                        return country.blocked === 1 ? (
                                            <div
                                                key={country.code}
                                                className='dropdownSelect'
                                            >
                                                <div className='dropdownSelect__element'>
                                                    <div className='dropdownSelect__element__icon'>
                                                        <div className='flag__wrapper'>
                                                            <ReactCountryFlag
                                                                countryCode={
                                                                    country.code
                                                                }
                                                                svg
                                                                cdnUrl='https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/4x3/'
                                                                cdnSuffix='svg'
                                                                title={
                                                                    country.code
                                                                }
                                                                style={{
                                                                    width: '4em',
                                                                    height: '3em'
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className='dropdownSelect__element__name'>
                                                        {country.name}
                                                    </div>
                                                    <div
                                                        className='dropdownSelect__head__icon light small'
                                                        onClick={() => {
                                                            handleCountryDelete(
                                                                country.id
                                                            )
                                                        }}
                                                    >
                                                        <img
                                                            src={Icons.trashbin}
                                                            alt={t('delete')}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ) : blockedRegions.length > 0 ? (
                                            blockedRegions.map(region => (
                                                <div
                                                    key={region.code}
                                                    className='dropdownSelect'
                                                >
                                                    <div className='dropdownSelect__element'>
                                                        <div className='dropdownSelect__element__icon'>
                                                            <div className='flag__wrapper'>
                                                                <ReactCountryFlag
                                                                    countryCode={
                                                                        country.code
                                                                    }
                                                                    svg
                                                                    cdnUrl='https://cdnjs.cloudflare.com/ajax/libs/flag-icon-css/3.4.3/flags/4x3/'
                                                                    cdnSuffix='svg'
                                                                    title={
                                                                        country.code
                                                                    }
                                                                    style={{
                                                                        width: '4em',
                                                                        height: '3em'
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className='dropdownSelect__element__name'>
                                                            {region.name},{' '}
                                                            {country.name}
                                                        </div>
                                                        <div
                                                            className='dropdownSelect__head__icon light small'
                                                            onClick={() => {
                                                                handleRegionDelete(
                                                                    region.id
                                                                )
                                                            }}
                                                        >
                                                            <img
                                                                src={
                                                                    Icons.trashbin
                                                                }
                                                                alt={t(
                                                                    'delete'
                                                                )}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        ) : null
                                    })}
                                </div>
                                {selectedCountry && (
                                    <Button
                                        text={t('block')}
                                        color='black'
                                        font='mont-16-bold'
                                        height='5'
                                        width='20'
                                        clickFn={handleAddBlock}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </>
            ) : (
                ''
            )}
        </WithHeaderSection>
    )
}

const ConfirmationDialog = styled.div`
    font-size: 1.4rem;
    color: ${colors.stormyBlue};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    align-self: stretch;
    text-align: center;
    padding-top: 5.6rem;

    img {
        max-width: 13rem;
        margin-bottom: 1.6rem;
        display: block;
        width: 100%;
    }

    h3 {
        font-size: 2.4rem;
        font-weight: 700;
        color: ${colors.blueDark};
        line-height: 3rem;
        margin: 0;
        margin-bottom: 1.6rem;
    }

    p {
        margin-bottom: 5.6rem;
        max-width: 22rem;
        line-height: 1.6rem;
    }
`
